import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import { virtualQrVerifyAction } from '../redux/actions/phoneLookupActions';
import { VIRTUAL_QR_VERIFY_RESET } from '../redux/constants/phoneLookupConstants';
import { createUserAction, getOTp } from '../redux/actions/userActions';
import Loading from '../components/Loading';

export default function PaymentReturnScreen() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isVerifying, setIsVerifying] = useState(true);
    const [verificationAttempts, setVerificationAttempts] = useState(0);
    
    const { 
        verificationData, 
        loading: verifyLoading, 
        error: verifyError, 
        success: verifySuccess 
    } = useSelector(state => state.virtualQrVerify);

    const { 
        loading: createUserLoading, 
        success: createUserSuccess, 
        error: createUserError, 
        data: createUserData 
    } = useSelector(state => state.createUser);

    const { 
        loading: otpLoading, 
        success: otpSuccess, 
        error: otpError 
    } = useSelector(state => state.getOtp);

    // Handle successful verification
    useEffect(() => {
        if (verifySuccess && verificationData) {
            setIsVerifying(false);
        
            const patientData = verificationData?.data || verificationData?.data.data;
            // Clean up sessionStorage
            sessionStorage.removeItem('paymentData');
            sessionStorage.removeItem('patient');
            // Show success confirmation
            swal({
                title: "Payment Successful!",
                text: "Your payment has been verified successfully. Would you like to proceed with creating your account now?",
                icon: "success",
                buttons: {
                    cancel: {
                        text: "Register Later",
                        value: false,
                        visible: true,
                        className: "swal-button--cancel",
                    },
                    confirm: {
                        text: "Create Account",
                        value: true,
                        visible: true,
                        className: "swal-button--confirm",
                    }
                }
            }).then((willRegister) => {
                if (willRegister) {
                    console.log('Patient data:', patientData);
                    console.log('Patient system_id:', patientData.system_id);
                    let system_id = patientData.system_id || patientData?.data?.system_id; 
                    if (!system_id) {
                        console.error('Patient system_id is missing:',);
                        toast.error('Patient information is incomplete. Please try again.');
                        navigate('/register');
                        return;
                    }
                    
                    dispatch(createUserAction(system_id));
                } else {
                    swal({
                        title: "Registration Reminder",
                        text: "You can register anytime using your payment reference. Keep this safe for future reference.",
                        icon: "info",
                        button: "Got it!",
                    }).then(() => {
                        navigate('/register');
                    });
                }
            });
        }
    }, [verifySuccess, verificationData, navigate, dispatch]);

    // Handle createUser success - trigger OTP
    useEffect(() => {
        if (createUserSuccess && createUserData) {
            const patientData = createUserData.data?.patient;
            if (patientData) {
                console.log('User created successfully, sending OTP:', patientData);
                // Trigger OTP sending
                dispatch(getOTp(patientData.system_id, patientData.mobile));
            }
        }
        
        if (createUserError) {
            console.error('Create user failed:', createUserError);
            
            // Provide specific error messages based on the error
            let errorMessage = 'Failed to create user account. Please try again.';
            if (createUserError.message?.includes('File number is required')) {
                errorMessage = 'Patient file number is missing. Please contact support.';
            } else if (createUserError.message) {
                errorMessage = createUserError.message;
            }
            
            toast.error(errorMessage);
            
            // Reset createUser state on error
            dispatch({ type: 'CREATE_USER_RESET' });
            
            // Navigate back to register if it's a critical error
            if (createUserError.message?.includes('File number is required')) {
                setTimeout(() => {
                    navigate('/register');
                }, 2000);
            }
        }
    }, [createUserSuccess, createUserData, createUserError, dispatch]);

    // Handle OTP success - navigate to success page
    useEffect(() => {
        if (otpSuccess && createUserSuccess && createUserData) {
            const patientData = createUserData.data?.patient;
            if (patientData) {
                console.log('OTP sent successfully, navigating to success page');
                
                // Reset states before navigation
                dispatch({ type: 'CREATE_USER_RESET' });
                dispatch({ type: 'GET_OTP_RESET' });
                dispatch({ type: VIRTUAL_QR_VERIFY_RESET });
                
                // Navigate to success page with patient data
                navigate("/success", { state: patientData });
            }
        }
        
        if (otpError) {
            console.error('OTP sending failed:', otpError);
            toast.error('Failed to send OTP. Please try again.');
            // Reset OTP state on error
            dispatch({ type: 'GET_OTP_RESET' });
        }
    }, [otpSuccess, otpError, createUserSuccess, createUserData, navigate, dispatch]);

    // Handle verification error
    useEffect(() => {
        if (verifyError && !verifyLoading) {
            console.error('Payment verification error:', verifyError);
            
            if (verificationAttempts < 20) {
                // Retry verification
                setTimeout(() => {
                    const storedPaymentData = sessionStorage.getItem('paymentData');
                    if (storedPaymentData) {
                        const paymentData = JSON.parse(storedPaymentData);
                        const reference = paymentData.reference;
                        if (reference) {
                            console.log(`Retrying payment verification attempt ${verificationAttempts + 1}`);
                            dispatch(virtualQrVerifyAction(reference));
                            setVerificationAttempts(prev => prev + 1);
                        }
                    }
                }, 3000);
            } else {
                // Max attempts reached
                setIsVerifying(false);
                
                swal({
                    title: "Payment Verification Timeout",
                    text: "We couldn't verify your payment automatically. Please contact support with your payment reference if money was deducted.",
                    icon: "warning",
                    button: "Contact Support",
                }).then(() => {
                    // Navigate back to register screen
                    navigate('/register');
                });
            }
        }
    }, [verifyError, verifyLoading, verificationAttempts, dispatch, navigate]);

    // Start verification when component mounts
    useEffect(() => {
        const storedPaymentData = sessionStorage.getItem('paymentData');
        
        if (!storedPaymentData) {
            toast.error('Payment reference not found');
            navigate('/register');
            return;
        }

        const paymentData = JSON.parse(storedPaymentData);
        const reference = paymentData.reference;
        
        if (!reference) {
            toast.error('Payment reference not found');
            navigate('/register');
            return;
        }

        // Reset verification state
        dispatch({ type: VIRTUAL_QR_VERIFY_RESET });
        
        // Start verification
        console.log('Starting payment verification for reference:', reference);
        dispatch(virtualQrVerifyAction(reference));
        
    }, [dispatch, navigate]);

    // Show full-screen loading during user creation or OTP sending
    if (createUserLoading || otpLoading) {
        return (
            <Loading 
                fullScreen={true}
                variant="medical"
                size="large"
                message={createUserLoading ? "Creating your account..." : "Sending OTP to your phone..."}
            />
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                {/* Loading Animation */}
                <div className="mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 relative">
                        <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                </div>

                {/* Status Message */}
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                    {isVerifying ? 'Verifying Payment...' : 'Processing...'}
                </h2>
                
                <p className="text-gray-600 mb-6">
                    {isVerifying 
                        ? `Please wait while we verify your payment. This may take a few moments.`
                        : 'Almost done...'
                    }
                </p>

                {/* Progress Indicator */}
                {isVerifying && (
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-center space-x-2 text-blue-600">
                            <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium">
                                Verification attempt {verificationAttempts + 1} of 20
                            </span>
                        </div>
                    </div>
                )}

                {/* Security Notice */}
                <div className="mt-6 text-xs text-gray-500">
                    <p>🔒 Your payment is being processed securely</p>
                </div>
            </div>
        </div>
    );
}